import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { BaseScheduledNode } from './base-scheduled-node';
import { useRevealBoundary } from './animation-schedule-context';

export function getAnimationUnitId(node, type) {
  const offset = node?.position?.start?.offset;

  return `${type}:${offset ?? 'unknown'}`;
}

export function MarkdownScheduledNode({
  as: Component,
  unitId,
  isAnimationDisabled = false,
  children = null,
  revealDelay = 0,
  blocksFollowing = false,
  className = '',
  reportAsRevealLeaf = false,
  ...restProps
}) {
  const elementRef = useRef(null);
  const { reportRevealLeaf } = useRevealBoundary();

  const onReveal = useCallback(() => {
    if (reportAsRevealLeaf) {
      reportRevealLeaf(elementRef.current);
    }
  }, [reportAsRevealLeaf, reportRevealLeaf]);

  const componentProps =
    typeof Component === 'string'
      ? { ...restProps, ...(reportAsRevealLeaf ? { ref: elementRef } : {}) }
      : { ...restProps, isAnimationDisabled, ...(reportAsRevealLeaf ? { elementRef } : {}) };

  return (
    <BaseScheduledNode
      unitId={unitId}
      isAnimationDisabled={isAnimationDisabled}
      revealDelay={revealDelay}
      blocksFollowing={blocksFollowing}
      onReveal={onReveal}
    >
      {({ visible }) => {
        const structuralClassName = `${className || ''}${visible ? '' : ' markdown-structural-hidden'}`.trim();

        return (
          <Component {...componentProps} {...(structuralClassName ? { className: structuralClassName } : {})}>
            {children}
          </Component>
        );
      }}
    </BaseScheduledNode>
  );
}

MarkdownScheduledNode.propTypes = {
  as: PropTypes.elementType.isRequired,
  unitId: PropTypes.string.isRequired,
  isAnimationDisabled: PropTypes.bool,
  children: PropTypes.node,
  revealDelay: PropTypes.number,
  blocksFollowing: PropTypes.bool,
  className: PropTypes.string,
  reportAsRevealLeaf: PropTypes.bool,
};
